import { useEffect, useState } from "react";
import { CreditCard, Loader2, CheckCircle2, ShieldCheck, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import PaymentMethodChip from "./PaymentMethodChip";
import { api } from "../lib/config";

// Payment methods that resolve to a Stripe Checkout flow. Keep in sync
// with `_STRIPE_PAYMENT_METHODS` in backend/server.py. `cash_on_arrival`
// is included because it's an INTENT — customer promised to pay in cash
// on the day. If the job's done and no cash was collected, they need an
// online fallback (iter73).
const STRIPE_METHODS = new Set([
  "stripe", "card", "credit_card", "applepay", "googlepay",
  "cash_on_arrival",
]);

// iter218 fallback — hard-coded so the disclosure ALWAYS renders even
// if `/api/payments/autopay-consent` is slow or fails. Backend is the
// source of truth on the version stamp.
const FALLBACK_CONSENT_TEXT = "By completing this payment, you authorize JDSN Lawn Care to securely save your payment method and automatically charge it for future recurring services. Your saved payment method will be used for future visits unless you cancel your recurring service or update your payment method.";

/**
 * PayNowInline — invoice payment UI. iter219 (2026-03-02) aligned this
 * visual language with the customer self-booking payment screen
 * (`PaymentMethodStep.jsx`): tip picker → "Pay $X your way" card →
 * Credit/Debit selector → "Secure card checkout powered by Stripe" info
 * panel → prominent green Payment Authorization block → big pill CTA.
 *
 * Owner directive: the Payment Authorization disclosure is now shown to
 * EVERY invoice payer (recurring + one-off), because backend iter218
 * saves the PaymentMethod to the customer's JDSN Stripe Customer on
 * every invoice payment and enables AutoPay for future recurring
 * services. No separate authorization link is ever needed after the
 * invoice has been paid with this disclosure visible.
 *
 * Props:
 *   booking – the full booking object from the customer API
 *   onPaid  – optional callback fired after redirect to Stripe.
 */
export default function PayNowInline({ booking, onPaid }) {
  const [loading, setLoading] = useState(false);
  const isRecurring = Boolean(booking?.frequency && booking.frequency !== "one_time");

  // iter201 — Optional tip. `null` = no tip, or a positive number for
  // the tip amount. Tips are NEVER auto-applied to future recurring
  // visits (owner rule) — the customer must choose each time.
  const [tipChoice, setTipChoice] = useState("none");
  const [customTip, setCustomTip] = useState("");
  const _selectedTip = (() => {
    if (tipChoice === "none") return 0;
    if (tipChoice === "custom") {
      const n = Number(customTip);
      return Number.isFinite(n) && n > 0 ? Math.min(500, Math.round(n * 100) / 100) : 0;
    }
    const n = Number(tipChoice);
    return Number.isFinite(n) ? n : 0;
  })();
  const _servicePrice = Number(booking?.price || 0);
  const _grandTotal = Math.round((_servicePrice + _selectedTip) * 100) / 100;
  const _grandStr = _grandTotal.toFixed(2);

  // iter219 — Fetch the exact disclosure copy from the backend so the
  // text shown here matches the audit stamp on user.autopay_consent_text.
  const [consentText, setConsentText] = useState(FALLBACK_CONSENT_TEXT);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/payments/autopay-consent");
        if (!cancelled && data?.text) setConsentText(data.text);
      } catch { /* fallback stays */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!booking) return null;
  const status = (booking.status || "").toLowerCase();
  const paymentStatus = (booking.payment_status || "").toLowerCase();
  const method = (booking.payment_method || "").toLowerCase();

  if (status !== "completed") return null;

  // Already paid → confirmation strip.
  if (paymentStatus === "paid") {
    const paidAt = booking.payment_completed_at || booking.paid_at || booking.updated_at;
    const paidPretty = paidAt ? formatDateTime(paidAt) : null;
    return (
      <div
        className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-800"
        data-testid={`paid-confirmation-${booking.booking_id}`}
      >
        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
        <div className="text-[13px] leading-tight">
          <div className="font-extrabold">Paid</div>
          {paidPretty && (
            <div className="text-[11px] font-medium text-emerald-700/80">
              Payment completed on {paidPretty}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Non-Stripe method → chip only.
  if (method && !STRIPE_METHODS.has(method)) {
    return (
      <div
        className="mt-3 flex items-center gap-2 flex-wrap"
        data-testid={`offline-payment-info-${booking.booking_id}`}
      >
        <span className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500">
          Pay via
        </span>
        <PaymentMethodChip method={method} methodLabel={booking.payment_method_label} />
      </div>
    );
  }

  const handleClick = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/pay-now`, {
        // iter218 — backend forces authorize_autopay=true on every
        // invoice payment so the disclosure shown here is honored.
        authorize_autopay: true,
        tip_amount: _selectedTip,
      });
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.assign(data.url);
      if (onPaid) onPaid();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message)
        ? detail.message
        : (typeof detail === "string" ? detail : "Couldn't open checkout. Please try again.");
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div className="mt-3 space-y-4" data-testid={`pay-now-block-${booking.booking_id}`}>
      {/* Orange "Payment Due" badge above the payment card */}
      <div className="flex items-center gap-1.5">
        <span
          className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] rounded-full border bg-orange-50 text-orange-800 border-orange-200 px-2 py-0.5"
          data-testid={`payment-due-badge-${booking.booking_id}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500" aria-hidden />
          Payment Due
        </span>
      </div>

      {/* Tip card — mirrors PaymentMethodStep layout */}
      <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 pb-2">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#047857] font-extrabold">
            Optional · Show the crew some love
          </div>
          <h3 className="mt-1 text-lg sm:text-xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]">
            Leave a tip <span className="text-gray-400 text-sm font-bold">(optional)</span>
          </h3>
          <p className="mt-1 text-[13px] text-gray-600">
            100% of tips go straight to the crew who serviced your yard.
          </p>
        </div>
        <div className="p-4 sm:p-5 pt-2" data-testid={`tip-section-${booking.booking_id}`}>
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: "5",      label: "$5" },
              { key: "10",     label: "$10" },
              { key: "20",     label: "$20" },
            ].map((o) => {
              const active = tipChoice === o.key;
              return (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setTipChoice(o.key)}
                  data-testid={`tip-opt-${o.key}-${booking.booking_id}`}
                  className={`h-12 rounded-2xl font-extrabold text-sm border-2 transition ${
                    active
                      ? "bg-[#047857] text-white border-[#047857]"
                      : "bg-white text-gray-800 border-gray-200 hover:border-emerald-300"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setTipChoice("custom")}
              data-testid={`tip-opt-custom-${booking.booking_id}`}
              className={`h-11 rounded-2xl font-extrabold text-sm border-2 transition ${
                tipChoice === "custom"
                  ? "bg-[#047857] text-white border-[#047857]"
                  : "bg-white text-gray-800 border-gray-200 hover:border-emerald-300"
              }`}
            >
              Custom
            </button>
            <button
              type="button"
              onClick={() => { setTipChoice("none"); setCustomTip(""); }}
              data-testid={`tip-opt-none-${booking.booking_id}`}
              className={`h-11 rounded-2xl font-extrabold text-sm border-2 transition ${
                tipChoice === "none"
                  ? "bg-gray-800 text-white border-gray-800"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
              }`}
            >
              No Tip
            </button>
          </div>
          {tipChoice === "custom" && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm font-bold text-gray-500">$</span>
              <input
                type="number"
                inputMode="decimal"
                min="0" max="500" step="0.01"
                value={customTip}
                onChange={(e) => setCustomTip(e.target.value)}
                placeholder="Enter tip amount"
                className="flex-1 h-11 px-3 rounded-xl border-2 border-gray-200 focus:border-emerald-400 outline-none text-sm font-bold"
                data-testid={`tip-custom-input-${booking.booking_id}`}
              />
            </div>
          )}
          {_selectedTip > 0 && (
            <div className="mt-2 text-[11px] text-emerald-800 font-bold" data-testid={`tip-summary-${booking.booking_id}`}>
              Service ${_servicePrice.toFixed(2)} + Tip ${_selectedTip.toFixed(2)} = <span className="text-emerald-900">${_grandStr}</span>
            </div>
          )}
        </div>
      </div>

      {/* Payment card — mirrors PaymentMethodStep layout */}
      <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 pb-2">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#047857] font-extrabold">
            Choose how you&apos;d like to pay
          </div>
          <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]">
            Pay <span className="text-[#047857]">${_grandStr}</span> your way
          </h2>
          <p className="mt-1 text-[13px] text-gray-600">
            Secure card checkout powered by Stripe. Apple Pay and Google Pay appear automatically on supported devices.
          </p>
        </div>

        <div className="p-4 sm:p-5 pt-2">
          <div className="rounded-2xl border border-gray-200 overflow-hidden"
               data-testid={`pay-method-stripe-${booking.booking_id}`}>
            <div className="w-full flex items-center gap-3 p-4 bg-gray-50">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-extrabold flex-shrink-0"
                   style={{ background: "#635BFF" }}>
                <CreditCard className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold text-gray-900 leading-tight">Credit / Debit Card</div>
                <div className="text-xs text-gray-500 truncate">
                  Visa · Mastercard · Amex · Apple Pay · Google Pay
                </div>
              </div>
              <div className="w-5 h-5 rounded-full border-2 flex-shrink-0 border-[#047857] bg-[#047857]">
                <Check className="w-4 h-4 text-white" />
              </div>
            </div>

            <div className="px-4 pb-5 pt-1 border-t border-gray-100 bg-white">
              <div className="mt-3 rounded-xl bg-[#F4F6F5] border border-gray-200 p-4 text-sm text-gray-700 leading-relaxed">
                Secure card checkout powered by Stripe. You&apos;ll be redirected to enter card details — your payment is confirmed on return.
              </div>

              {/* iter219 — Payment authorization disclosure shown to
                  EVERY invoice payer (owner directive 2026-03-02).
                  Recurring + one-off invoices alike. */}
              <div className="mt-4 flex items-start gap-3 p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50/80"
                   data-testid={`autopay-authorization-disclosure-${booking.booking_id}`}>
                <ShieldCheck className="w-5 h-5 text-[#047857] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-extrabold text-emerald-900 leading-tight mb-1"
                       data-testid={`autopay-authorization-title-${booking.booking_id}`}>
                    Payment authorization
                  </div>
                  <div className="text-[13px] text-emerald-900/90 leading-relaxed"
                       data-testid={`autopay-authorization-body-${booking.booking_id}`}>
                    {consentText}
                  </div>
                  {!isRecurring && (
                    <div className="mt-2 text-[12px] text-emerald-900/70">
                      Your card is saved to your account so we can charge it if
                      you book a recurring service later. You can remove it
                      anytime from your Profile.
                    </div>
                  )}
                </div>
              </div>

              <Button
                type="button"
                onClick={handleClick}
                disabled={loading}
                className="mt-4 w-full h-13 rounded-full bg-gradient-to-br from-[#10B981] to-[#047857] text-white font-extrabold text-base shadow-[0_8px_24px_rgba(4,120,87,0.28)]"
                data-testid={`pay-now-btn-${booking.booking_id}`}
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Opening secure checkout…</>
                ) : (
                  <><CreditCard className="w-4 h-4 mr-1.5" /> Pay ${_grandStr} with Card{_selectedTip > 0 ? ` (incl. $${_selectedTip.toFixed(2)} tip)` : ""}</>
                )}
              </Button>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">
                <ShieldCheck className="w-3 h-3" /> Stripe · Visa · Mastercard · Amex · Apple Pay · Google Pay
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}
